import { z } from 'zod';
import { SheetIdSchema, WorkbookIdSchema } from './entity-ids';

/**
 * Persisted spreadsheet cell/sheet shapes: a strict subset of Univer's
 * IWorksheetData. Styles are inline per cell (`s` is always an IStyleData
 * object, never a style-registry ref id) so a sheet's Json row is
 * self-contained; the client dereferences Univer style refs before POSTing.
 */

export const WorkbookCellValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
]);
export type WorkbookCellValue = z.infer<typeof WorkbookCellValueSchema>;

export const WorkbookCellSchema = z.object({
  /** Computed/static value; plain text for rich-text cells. */
  v: WorkbookCellValueSchema.optional(),
  /** Formula source, always starting with '='. Never a shared-formula ref. */
  f: z.string().optional(),
  /**
   * Univer rich-text document (IDocumentData) for cells with line breaks or
   * mixed formatting. Readers that only need text use `v`.
   */
  p: z.record(z.string(), z.unknown()).optional(),
  /** Inline IStyleData (bold/italic/color/background/number format/...). */
  s: z.record(z.string(), z.unknown()).optional(),
  /** Univer CellValueType, preserved verbatim. */
  t: z.number().int().optional(),
});
export type WorkbookCell = z.infer<typeof WorkbookCellSchema>;

/**
 * Field-level cell change: a present field sets it (null clears it), an
 * absent field is left alone. `s` replaces the whole style; `sp` changes
 * only the style keys it names (null removes one), and its `bd` changes
 * only the border sides it names, so two people styling the same cell at
 * once both keep their change.
 */
export const WorkbookCellPatchSchema = z.object({
  v: WorkbookCellValueSchema.nullable().optional(),
  f: z.string().nullable().optional(),
  p: z.record(z.string(), z.unknown()).nullable().optional(),
  s: z.record(z.string(), z.unknown()).nullable().optional(),
  sp: z.record(z.string(), z.unknown()).optional(),
  t: z.number().int().nullable().optional(),
});
export type WorkbookCellPatch = z.infer<typeof WorkbookCellPatchSchema>;

/** Sparse row -> column -> cell records, keyed by stringified 0-based indexes. */
export const WorkbookCellDataSchema = z.record(
  z.string(),
  z.record(z.string(), WorkbookCellSchema)
);
export type WorkbookCellData = z.infer<typeof WorkbookCellDataSchema>;

export const WorkbookRowSchema = z.object({
  /** Height set by the user (px). */
  h: z.number().optional(),
  /** Height the grid measured for the row's content (px). */
  ah: z.number().optional(),
  /** 1 = the row height follows its content ("Fit for data"). */
  ia: z.number().int().optional(),
  hd: z.number().optional(),
});
export type WorkbookRow = z.infer<typeof WorkbookRowSchema>;

export const WorkbookColumnSchema = z.object({
  w: z.number().optional(),
  hd: z.number().optional(),
});
export type WorkbookColumn = z.infer<typeof WorkbookColumnSchema>;

export const WorkbookRowDataSchema = z.record(z.string(), WorkbookRowSchema);

export const WorkbookColumnDataSchema = z.record(
  z.string(),
  WorkbookColumnSchema
);

export const WorkbookMergeSchema = z.object({
  startRow: z.number().int().nonnegative(),
  endRow: z.number().int().nonnegative(),
  startColumn: z.number().int().nonnegative(),
  endColumn: z.number().int().nonnegative(),
});
export type WorkbookMerge = z.infer<typeof WorkbookMergeSchema>;

export const WorkbookFreezeSchema = z.object({
  xSplit: z.number().int(),
  ySplit: z.number().int(),
  startRow: z.number().int(),
  startColumn: z.number().int(),
});
export type WorkbookFreeze = z.infer<typeof WorkbookFreezeSchema>;

export const WorkbookSheetDataSchema = z.object({
  rowCount: z.number().int().positive(),
  columnCount: z.number().int().positive(),
  cellData: WorkbookCellDataSchema,
  rowData: WorkbookRowDataSchema.optional(),
  columnData: WorkbookColumnDataSchema.optional(),
  mergeData: z.array(WorkbookMergeSchema).optional(),
  freeze: WorkbookFreezeSchema.optional(),
  /** Tab color as a CSS color string. */
  tabColor: z.string().optional(),
  /** 1 = the tab is hidden. */
  hidden: z.number().int().optional(),
  /** 0 = gridlines are turned off (absent means shown). */
  showGridlines: z.number().int().optional(),
});
export type WorkbookSheetData = z.infer<typeof WorkbookSheetDataSchema>;

export const DEFAULT_SHEET_ROW_COUNT = 100;
export const DEFAULT_SHEET_COLUMN_COUNT = 26;

export function createEmptySheetData(): WorkbookSheetData {
  return {
    rowCount: DEFAULT_SHEET_ROW_COUNT,
    columnCount: DEFAULT_SHEET_COLUMN_COUNT,
    cellData: {},
  };
}

export const SheetSnapshotSchema = z.object({
  id: SheetIdSchema,
  workbookId: WorkbookIdSchema,
  order: z.number().int().nonnegative(),
  name: z.string(),
  data: WorkbookSheetDataSchema,
});
export type SheetSnapshot = z.infer<typeof SheetSnapshotSchema>;
